import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Divider,
  Tabs,
  Typography,
  Stack,
  IconButton,
  Tab as MuiTab,
  TabsProps,
} from "@mui/material";
import DragDropTabs from "./components/DragDropTabs";
import { Edit } from "@mui/icons-material";
import { useLocalStorageTabs } from "./useLocalStorageTabs";
import { Tab } from "./components/types";

interface TabbedInterfaceProps {
  tabs: Tab[];
  defaultPath: string;
  translate: (_key: string) => string;
  editable?: boolean;
  editableLocationKey?: string;
  tabCounts?: Record<string, number>;
  headerActions?: React.ReactElement;
  renderTab?: (_tab: Tab, _number: number, _index: number) => React.ReactNode;
  tabsVariant?: TabsProps["variant"];

  /**
   * Enables tab persistence via localStorage.
   * Default: true
   */
  onActiveTabChange?: (_path: string) => void;
  persistTabs?: boolean;
}

export const TabbedInterface: React.FC<TabbedInterfaceProps> = ({
  tabs,
  defaultPath,
  translate,
  editable = false,
  editableLocationKey,
  tabCounts,
  headerActions,
  renderTab,
  tabsVariant = "scrollable",
  persistTabs = true,
  onActiveTabChange,
}) => {
  const {
    allTabs,
    visibleTabs,
    changeSelectedTab,
    currentSelectedTabIndex,
    updateTabs,
  } = useLocalStorageTabs(
    tabs,
    editable,
    persistTabs,
    defaultPath,
    editableLocationKey,
  );
  const [edit, setEdit] = useState<boolean>(false);

  const handleChange = (_: React.SyntheticEvent, selectedIndex: number) => {
    changeSelectedTab(selectedIndex);

    const selectedPath = visibleTabs[selectedIndex]?.path;
    onActiveTabChange?.(selectedPath);
  };

  useEffect(() => {
    const activePath = visibleTabs[currentSelectedTabIndex]?.path;
    if (activePath) {
      onActiveTabChange?.(activePath);
    }
  }, [currentSelectedTabIndex, visibleTabs, onActiveTabChange]);

  const handleSaveEdit = (editedTabs: Tab[]) => {
    updateTabs(editedTabs);
    setEdit(false);
  };

  const handleCloseEdit = () => {
    setEdit(false);
  };

  return (
    <Container component={Card} maxWidth={false} sx={{ padding: "8px" }}>
      <Stack
        direction="row"
        justifyContent={headerActions ? "space-between" : "center"}
        alignItems={"center"}
        marginTop="8px"
        marginBottom="8px"
      >
        <Typography variant="h6" textAlign="center">
          {translate("title")}
        </Typography>

        {headerActions}
      </Stack>

      <Stack direction={"row"} spacing={1}>
        {edit ? (
          <DragDropTabs
            translate={translate}
            activeTabPath={visibleTabs[currentSelectedTabIndex].path}
            tabs={allTabs}
            onSaveTabs={handleSaveEdit}
            onClose={handleCloseEdit}
          />
        ) : (
          <Tabs
            value={currentSelectedTabIndex}
            onChange={handleChange}
            variant={tabsVariant}
            sx={{ flexGrow: 1 }}
          >
            {visibleTabs.map((tab, index) => {
              const customTab = renderTab
                ? renderTab(tab, tabCounts?.[tab.path] ?? 0, index)
                : null;
              return (
                customTab ?? (
                  <MuiTab
                    key={tab.path}
                    label={
                      translate(tab.label) +
                      (tabCounts?.[tab.path] !== null &&
                      tabCounts?.[tab.path] !== undefined
                        ? ` (${tabCounts[tab.path]})`
                        : "")
                    }
                    value={index}
                    sx={{ textTransform: "none" }}
                  />
                )
              );
            })}
          </Tabs>
        )}

        {editable && !edit && (
          <Stack direction={"row"} alignItems={"center"}>
            <IconButton color="primary" onClick={() => setEdit(true)}>
              <Edit fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ margin: "10px 0px" }} />
      {(() => {
        const activePath = visibleTabs[currentSelectedTabIndex].path;
        const liveTab = tabs.find((t) => t.path === activePath);

        if (!liveTab) return null;

        if ("render" in liveTab && liveTab.render) {
          return liveTab.render();
        }

        return liveTab.component;
      })()}
    </Container>
  );
};
